/**
 * BradfordFactorDashboard.jsx
 * 
 * Manager dashboard showing Bradford Factor scores for all employees.
 * Displays alerts, filters by level, and shows individual employee details.
 * 
 * Bradford Factor: B = S² × D
 * - S = Number of separate absence spells (instances)
 * - D = Total days absent
 * 
 * Higher scores indicate more disruptive absence patterns.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';

// Professional SVG Icons
const Icons = {
  ArrowLeft: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"></line>
      <polyline points="12,19 5,12 12,5"></polyline>
    </svg>
  ),
  AlertTriangle: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
      <line x1="12" y1="9" x2="12" y2="13"></line>
      <line x1="12" y1="17" x2="12.01" y2="17"></line>
    </svg>
  ),
  Users: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  TrendingUp: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"></polyline>
      <polyline points="17,6 23,6 23,12"></polyline>
    </svg>
  ),
  Settings: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"></circle>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
    </svg>
  ),
  ChevronDown: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6,9 12,15 18,9"></polyline>
    </svg>
  ),
  ChevronUp: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18,15 12,9 6,15"></polyline>
    </svg>
  ),
  Info: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  ),
  Search: (props) => (
    <svg width="20" height="20" {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  ),
};

// Level colors and styling
const LEVEL_CONFIG = {
  green: { 
    color: '#22c55e', 
    bg: '#dcfce7', 
    label: 'No Concern',
    description: 'Absence pattern within acceptable limits'
  },
  amber: { 
    color: '#f59e0b', 
    bg: '#fef3c7', 
    label: 'Monitor',
    description: 'Consider informal discussion about attendance'
  },
  orange: { 
    color: '#f97316', 
    bg: '#ffedd5', 
    label: 'Warning',
    description: 'First written warning may be appropriate'
  },
  red: { 
    color: '#ef4444', 
    bg: '#fee2e2', 
    label: 'Serious',
    description: 'Final written warning may be appropriate'
  },
  critical: { 
    color: '#991b1b', 
    bg: '#fecaca', 
    label: 'Critical',
    description: 'Consider dismissal proceedings'
  },
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  header: {
    background: 'white',
    borderBottom: '1px solid #e2e8f0',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  content: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: '20px',
    marginBottom: '16px',
  },
  statCard: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  button: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
  },
  input: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    borderBottom: '2px solid #e2e8f0',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#334155',
  },
};

// Score bar component
const ScoreBar = ({ score, thresholds }) => {
  const maxScore = thresholds.critical * 1.2;
  const percentage = Math.min(100, (score / maxScore) * 100);
  
  let color = LEVEL_CONFIG.green.color;
  if (score >= thresholds.critical) color = LEVEL_CONFIG.critical.color;
  else if (score >= thresholds.red) color = LEVEL_CONFIG.red.color;
  else if (score >= thresholds.orange) color = LEVEL_CONFIG.orange.color;
  else if (score >= thresholds.amber) color = LEVEL_CONFIG.amber.color;
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '150px' }}>
      <div style={{
        flex: 1,
        height: '8px',
        background: '#e2e8f0',
        borderRadius: '4px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          background: color,
          borderRadius: '4px',
          transition: 'width 0.3s ease',
        }} />
      </div>
      <span style={{ fontWeight: '600', minWidth: '40px', textAlign: 'right' }}>{score}</span>
    </div>
  );
};

// Main Component
const BradfordFactorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ employees: [], summary: {} });
  const [alerts, setAlerts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [teamResponse, alertsResponse, settingsResponse] = await Promise.all([
        api.get('/bradford/team'),
        api.get('/bradford/alerts'),
        api.get('/bradford/settings'),
      ]);
      
      setData(teamResponse.data);
      setAlerts(alertsResponse.data.alerts || []);
      setSettings(settingsResponse.data);
    } catch (err) {
      console.error('Failed to load Bradford Factor data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeDetail = async (employeeId) => {
    try {
      const response = await api.get(`/bradford/employee/${employeeId}`);
      setEmployeeDetail(response.data);
    } catch (err) {
      console.error('Failed to load employee detail:', err);
    }
  };

  const handleEmployeeClick = (employeeId) => {
    if (expandedEmployee === employeeId) {
      setExpandedEmployee(null);
      setEmployeeDetail(null);
    } else {
      setExpandedEmployee(employeeId);
      loadEmployeeDetail(employeeId);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/bradford/settings', {
        threshold_green: settings.thresholds.green,
        threshold_amber: settings.thresholds.amber,
        threshold_orange: settings.thresholds.orange,
        threshold_red: settings.thresholds.red,
        threshold_critical: settings.thresholds.critical,
      });
      setShowSettings(false);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Filter employees
  const filteredEmployees = data.employees?.filter(emp => {
    if (searchTerm && !emp.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (levelFilter !== 'all' && emp.level !== levelFilter) {
      return false;
    }
    return true;
  }) || [];

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate(-1)} style={{ ...styles.button, background: '#f1f5f9', color: '#475569' }}>
            <Icons.ArrowLeft />
          </button>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Bradford Factor</h1>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#0d9488',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={{ ...styles.button, background: '#f1f5f9', color: '#475569' }}>
          <Icons.ArrowLeft />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>Bradford Factor</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
            Absence pattern analysis • B = S² × D
          </p>
        </div>
        <button 
          onClick={() => setShowSettings(true)}
          style={{ ...styles.button, background: '#f1f5f9', color: '#475569' }}
        >
          <Icons.Settings />
          Settings
        </button>
      </div>

      <div style={styles.content}>
        {/* Alerts Section */}
        {alerts.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icons.AlertTriangle style={{ color: '#f59e0b' }} />
              Attention Required ({alerts.length})
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
              {alerts.slice(0, 6).map(alert => (
                <div 
                  key={alert.employee_id}
                  onClick={() => handleEmployeeClick(alert.employee_id)}
                  style={{
                    ...styles.card,
                    cursor: 'pointer',
                    borderLeft: `4px solid ${LEVEL_CONFIG[alert.level].color}`,
                    marginBottom: 0,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{alert.name}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>{alert.job_title}</div>
                    </div>
                    <span style={{
                      ...styles.badge,
                      background: LEVEL_CONFIG[alert.level].bg,
                      color: LEVEL_CONFIG[alert.level].color,
                    }}>
                      {alert.score}
                    </span>
                  </div>
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>
                    {alert.spells} spell{alert.spells !== 1 ? 's' : ''} • {alert.days} day{alert.days !== 1 ? 's' : ''}
                  </div>
                  {alert.approaching_next && (
                    <div style={{
                      marginTop: '8px',
                      padding: '6px 10px',
                      background: '#fef3c7',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#92400e',
                    }}>
                      ⚠️ Approaching {alert.approaching_next} threshold
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          {Object.entries(LEVEL_CONFIG).map(([level, config]) => (
            <div 
              key={level}
              onClick={() => setLevelFilter(levelFilter === level ? 'all' : level)}
              style={{
                ...styles.statCard,
                cursor: 'pointer',
                border: levelFilter === level ? `2px solid ${config.color}` : '2px solid transparent',
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: config.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: config.color,
                fontWeight: '700',
                fontSize: '16px',
              }}>
                {data.summary?.[level] || 0}
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: config.color }}>{config.label}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {data.summary?.thresholds?.[level] || 0}+
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ ...styles.card, display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <Icons.Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...styles.input, width: '100%', paddingLeft: '40px' }}
            />
          </div>
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            style={{ ...styles.input, cursor: 'pointer' }}
          >
            <option value="all">All Levels</option>
            {Object.entries(LEVEL_CONFIG).map(([level, config]) => (
              <option key={level} value={level}>{config.label}</option>
            ))}
          </select>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Showing {filteredEmployees.length} of {data.employees?.length || 0} employees
          </div>
        </div>

        {/* Employee Table */}
        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Employee</th>
                <th style={styles.th}>Job Title</th>
                <th style={styles.th}>Score</th>
                <th style={styles.th}>Spells</th>
                <th style={styles.th}>Days</th>
                <th style={styles.th}>Level</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(emp => (
                <React.Fragment key={emp.employee_id}>
                  <tr 
                    onClick={() => handleEmployeeClick(emp.employee_id)}
                    style={{ cursor: 'pointer', background: expandedEmployee === emp.employee_id ? '#f8fafc' : 'transparent' }}
                  >
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600' }}>{emp.name}</div>
                    </td>
                    <td style={styles.td}>{emp.job_title || '-'}</td>
                    <td style={styles.td}>
                      <ScoreBar score={emp.score} thresholds={data.summary?.thresholds || {}} />
                    </td>
                    <td style={styles.td}>{emp.spells}</td>
                    <td style={styles.td}>{emp.days}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: LEVEL_CONFIG[emp.level]?.bg,
                        color: LEVEL_CONFIG[emp.level]?.color,
                      }}>
                        {LEVEL_CONFIG[emp.level]?.label}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', color: '#64748b' }}>
                          {emp.action?.substring(0, 30)}...
                        </span>
                        {expandedEmployee === emp.employee_id ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                      </div>
                    </td>
                  </tr>
                  {expandedEmployee === emp.employee_id && employeeDetail && (
                    <tr>
                      <td colSpan={7} style={{ padding: '0', background: '#f8fafc' }}>
                        <div style={{ padding: '20px', borderTop: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                            {/* Score Breakdown */}
                            <div>
                              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#64748b' }}>Score Breakdown</h4>
                              <div style={{ background: 'white', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                  <span>Spells (S)</span>
                                  <strong>{employeeDetail.spells}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                  <span>Days (D)</span>
                                  <strong>{employeeDetail.days}</strong>
                                </div>
                                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '8px', marginTop: '8px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Formula: S² × D</span>
                                    <strong>{employeeDetail.spells}² × {employeeDetail.days} = {employeeDetail.score}</strong>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Absence Spells */}
                            <div>
                              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#64748b' }}>
                                Absence Spells ({employeeDetail.absence_spells?.length || 0})
                              </h4>
                              <div style={{ background: 'white', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', maxHeight: '200px', overflow: 'auto' }}>
                                {employeeDetail.absence_spells?.length > 0 ? (
                                  employeeDetail.absence_spells.map((spell, idx) => (
                                    <div key={idx} style={{ 
                                      padding: '8px 0', 
                                      borderBottom: idx < employeeDetail.absence_spells.length - 1 ? '1px solid #f1f5f9' : 'none' 
                                    }}>
                                      <div style={{ fontSize: '13px', fontWeight: '600' }}>
                                        {new Date(spell.start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                        {spell.start !== spell.end && ` - ${new Date(spell.end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                                        {spell.days} day{spell.days !== 1 ? 's' : ''}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div style={{ color: '#64748b', fontSize: '13px' }}>No absences in this period</div>
                                )}
                              </div>
                            </div>

                            {/* Recommended Action */}
                            <div>
                              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#64748b' }}>Recommended Action</h4>
                              <div style={{ 
                                background: LEVEL_CONFIG[employeeDetail.level]?.bg, 
                                padding: '16px', 
                                borderRadius: '8px',
                                border: `1px solid ${LEVEL_CONFIG[employeeDetail.level]?.color}20`,
                              }}>
                                <div style={{ fontWeight: '600', color: LEVEL_CONFIG[employeeDetail.level]?.color, marginBottom: '8px' }}>
                                  {LEVEL_CONFIG[employeeDetail.level]?.label}
                                </div>
                                <div style={{ fontSize: '13px', color: '#334155' }}>
                                  {employeeDetail.action}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {filteredEmployees.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
              <Icons.Users style={{ width: '48px', height: '48px', marginBottom: '16px', opacity: 0.5 }} />
              <div style={{ fontWeight: '600' }}>No employees found</div>
              <div style={{ fontSize: '14px' }}>Try adjusting your search or filters</div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div style={{ ...styles.card, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Icons.Info style={{ color: '#3b82f6', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>About Bradford Factor</div>
              <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.5' }}>
                The Bradford Factor measures absence disruption using the formula <strong>B = S² × D</strong>, 
                where S is the number of separate absence spells and D is total days absent in the rolling 52-week period. 
                Frequent short absences score higher than fewer longer absences because they're more disruptive to operations. 
                Scores are guidelines only and should be considered alongside individual circumstances.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && settings && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>Bradford Factor Thresholds</h3>
            
            {Object.entries(LEVEL_CONFIG).filter(([k]) => k !== 'green').map(([level, config]) => (
              <div key={level} style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: config.color,
                  marginBottom: '6px' 
                }}>
                  <span style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: config.color,
                  }} />
                  {config.label} Threshold
                </label>
                <input
                  type="number"
                  value={settings.thresholds[level]}
                  onChange={(e) => setSettings({
                    ...settings,
                    thresholds: { ...settings.thresholds, [level]: parseInt(e.target.value) || 0 }
                  })}
                  style={{ ...styles.input, width: '100%' }}
                />
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  {config.description}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowSettings(false)}
                style={{ ...styles.button, flex: 1, background: '#f1f5f9', color: '#475569' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                style={{ 
                  ...styles.button, 
                  flex: 1, 
                  background: '#0d9488', 
                  color: 'white',
                  opacity: savingSettings ? 0.5 : 1,
                }}
              >
                {savingSettings ? 'Saving...' : 'Save Thresholds'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BradfordFactorDashboard;